<template>
  <NuxtLayout>
    <HeroHeader class="accommodationDetail__Header" :title-visible="false" :image="activeAccommodation.gallery[0]" stacked-elements>
      <div class="container heroHeader__StackedElementsContent">
        <div>
          <div class="accommodationDetail__HeaderLocation">
            <Icon name="location" />
            <span v-text="activeAccommodation.place.name" />
          </div>
          <h1 v-text="page.title" />
          <div v-if="activeAccommodation.category_rating" class="accommodationDetail__StarsWrapper">
            <Icon v-for="star in activeAccommodation.category_rating" name="star" />
          </div>
        </div>

        <div v-if="page.overlay_image" class="accommodationDetail__HeaderOverlayImage">
          <Flare :srcset="page.overlay_image" width="200" :alt="page.overlay_image" />
        </div>
      </div>
    </HeroHeader>

    <CMSField v-if="page?.content" :data="page.content" />
  </NuxtLayout>
</template>

<script setup>
  defineOptions({
    auth: 'aminess'
  });
  
  const props = defineProps({
    page: {}
  });

  useHead({
    htmlAttrs: {
      class: 'fixedHeader fixedHeader--WithoutTopPadding accommodationDetail',
    },
  });

  const { t, locales } = useI18n();
  const { fullPath } = useRoute();
  const { accommodationsData } = useSetup();
  const envVariables = useRuntimeConfig();
  let seoLocales = [];
  let seoAmenities = [];
  let seoPaymentMethods = [];
  let seoSocials = [];

  const activeAccommodation = computed(() => accommodationsData.value[props.page.id]);

  onMounted(() => {
    getThemeStyles(activeAccommodation.value);
  })

  onBeforeUnmount(() => {
    removeThemeStyles();
  })


  // seo
  if (locales?.value.length) {
    seoLocales = locales.value.map(locale => t(`languages.${ locale.code }`)).join(', ');
  }

  if (activeAccommodation?.value?.usp_verticals?.length) {
    seoAmenities = activeAccommodation.value.usp_verticals.map(amenity => {
      return { "@type": "LocationFeatureSpecification", "name": amenity.description || '' }
    });
  }

  if (props.page.accepted_payment_methods?.length) {
    seoPaymentMethods = props.page.accepted_payment_methods.map(paymentMethod => {
      return { "@type": "PaymentMethod", "url": paymentMethod.url || '' }
    });
  }

  for (const social of ['facebook_social', 'instagram_social']) {
    if (props.page[social]) {
      seoSocials.push(props.page[social]);
    }
  }
  
  useJsonld(() => ({
    "@context": "http://schema.org",
    "@type": "Hotel",
    "name": `${ activeAccommodation?.value?.name || '' }`,
    "url": `${ envVariables.public?.domain || '' }${ fullPath }`,
    "image": `${ activeAccommodation?.value?.gallery?.[0]?.meta?.url || '' }`,
    "logo": `${ activeAccommodation?.value?.brand?.dark_logo?.meta?.url || '' }`,
    "description": `${ props.page?.meta?.search_description || '' }`,
    "telephone": `${ activeAccommodation?.value?.phone || '' }`,
    "email": `${ activeAccommodation?.value?.email || '' }`,
    "sameAs": seoSocials,
    "audience": {
      "@type": "http://schema.org/PeopleAudience",
      "audienceType": "Travelers"
    },
    "availableLanguage": `${ seoLocales }`,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": `${ activeAccommodation?.value?.address || '' }`,
      "addressLocality": `${ activeAccommodation?.value?.place?.name || '' }`,
      "addressRegion": `${ activeAccommodation?.value?.region?.name || '' }`,
      "postalCode": `${ activeAccommodation?.value?.place?.zip || '' }`,
      "addressCountry": `${ activeAccommodation?.value?.country?.code || '' }`
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": `${ activeAccommodation?.value?.latitude || '' }`,
      "longitude": `${ activeAccommodation?.value?.longitude || '' }`
    },
    "hasMap": `https://www.google.com/maps/place/${ activeAccommodation?.value?.latitude },${ activeAccommodation?.value?.longitude }`,
    "checkinTime": `${ props.page.checkin_time || '' }`,
    "checkoutTime": `${ props.page.checkout_time || '' }`,
    "priceRange": `${ props.page.price_from || '' }`,
    "starRating": {
      "@type": "Rating",
      "ratingValue": `${ activeAccommodation?.value?.category_rating || '' }`,
      "bestRating": "5",
      "worstRating": "1"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": `${ props.page.rating_value || ''}`,
      "bestRating": `${ props.page.best_rating || ''}`,
      "worstRating": `${ props.page.worst_rating || ''}`,
      "ratingCount": `${ props.page.rating_count || ''}`
    },
    "acceptedPaymentMethod": seoPaymentMethods,
    "amenityFeature": seoAmenities,
    "petsAllowed": `${ props.page.pets_allowed ? 'True' : 'False' }`,
    "smokingAllowed": `${ props.page.smoking_allowed ? 'True' : 'False' }`,
  }))
</script>

<style lang="scss">
  @import url('@/assets/styles/pages/_accommodationDetail.scss');
</style>